import { FC, ReactElement, useState } from "react";
import { Box, CircularProgress, Grid, Stack } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { PictureUploader, TextInput } from "@/components";
import { StyledButton } from "../styled";
import { roles } from "@/roles";
import { useSnackbar, useUploadFile } from "@/hooks";
import { useUpdateCompanyInfoMutation } from "@/services";
import { ValidationPatterns } from "@/utils/form-validation";
import { useAppSelector } from "@/store";

interface ICompanyFormProps {
  user: IUser | null;
}

interface ICompanyDetails {
  companyName: string;
  companyEmail: string;
  businessAddress: string;
  companyLogo: File | null;
}

const CompanyForm: FC<ICompanyFormProps> = ({ user }): ReactElement => {
  const { companyEmail, businessAddress, companyLogo, companyName } =
    useAppSelector((state) => state.settings);

  const isDisabled =
    user?.userRole !== roles.TDCLIENT && user?.userRole !== roles.SPOWNER;

  const [logoPreview, setLogoPreview] = useState<string | undefined>(
    companyLogo
  );
  const { isLoading, uploadFile } = useUploadFile();

  const [updateCompanyInfo, { isLoading: companyInfoLoading }] =
    useUpdateCompanyInfoMutation();
  const { show } = useSnackbar();

  const {
    control,
    handleSubmit,

    setValue,
  } = useForm<ICompanyDetails>({
    defaultValues: {
      companyName,
      companyEmail,
      businessAddress,
      companyLogo: null,
    },
  });

  const handleLogoChange = (file: File | null) => {
    if (file) {
      const fileURL = URL.createObjectURL(file);
      setValue("companyLogo", file);
      setLogoPreview(fileURL);
    }
  };

  const onSubmit = async (data: ICompanyDetails) => {
    const { companyLogo, businessAddress, companyName } = data;
    try {
      let companyLogoKey;
      if (companyLogo) {
        const data = await uploadFile(companyLogo);
        companyLogoKey = data?.key || "";
      }

      await updateCompanyInfo({
        tenantData: {
          businessAddress,
          companyName,
          companyLogo: companyLogoKey,
        },
      }).unwrap();

      show("Company Info updated successfully!", "", "success");
    } catch (error: any) {
      show(error.message, "", "error");
    }
  };

  const isButtonLoading = isLoading || companyInfoLoading;

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <PictureUploader
        imgUrl={companyLogo ?? logoPreview}
        title="Company Logo"
        customPreviewType="circle"
        description="We recommend an image of at least 400x400."
        onChange={handleLogoChange}
        disabled={isDisabled}
      />
      <Grid
        container
        spacing={2}
        sx={{
          "& .MuiInputBase-root input": {
            px: 2,
            py: 1.25,
          },
        }}
      >
        <Grid item xs={6}>
          <Controller
            name="companyName"
            control={control}
            rules={{
              required: {
                value: true,
                message: "Company name is required.",
              },
            }}
            render={({ field: { onChange, ...rest } }) => (
              <TextInput
                {...rest}
                label="Company Name"
                color="success"
                handleChangeValue={onChange}
                disabled={isDisabled}
                type="text"
                placeholder="Enter Company name"
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            name="companyEmail"
            control={control}
            rules={{
              required: {
                value: true,
                message: "Company email is required.",
              },
              pattern: {
                value: ValidationPatterns.email,
                message: "Invalid email address.",
              },
            }}
            render={({ field: { onChange, ...rest } }) => (
              <TextInput
                {...rest}
                label="Company Email"
                color="success"
                handleChangeValue={onChange}
                disabled={true}
                type="email"
                placeholder="Enter Company email"
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="businessAddress"
            control={control}
            rules={{
              required: {
                value: true,
                message: "Business address is required.",
              },
            }}
            render={({ field: { onChange, ...rest } }) => (
              <TextInput
                {...rest}
                label="Business Address"
                color="success"
                handleChangeValue={onChange}
                disabled={isDisabled}
                type="text"
                placeholder="Enter Business address"
              />
            )}
          />
        </Grid>
      </Grid>
      <Stack direction="row" justifyContent="flex-end" mt={3.75} spacing={2}>
        <StyledButton
          variant="contained"
          type="submit"
          disabled={isDisabled || isButtonLoading}
          {...(isButtonLoading && {
            startIcon: <CircularProgress sx={{ color: "white" }} size={20} />,
          })}
        >
          Save Changes
        </StyledButton>
      </Stack>
    </Box>
  );
};

export default CompanyForm;
